import {
  addDownloadCenterRecord,
  collectKnownImageBytes,
  enqueueDownloadCenterJob,
  estimateClientZipBytes,
  isDownloadTaskCancelled,
  queueBrowserZipDownload,
  registerDownloadTaskAbort,
  shouldUseServerZipDownload,
  submitServerDownloadTask,
  updateDownloadCenterProgress,
} from '@/utils/downloadCenter';
import {
  resolveSignedDisplayForUrl,
  sanitizeManagedDisplayResult,
  warmDisplayCacheFromRecords,
  prefetchDisplayUrlsForListItems,
  isManagedStorageUrl,
} from '@/utils/imageDisplayAccess';
import { resolveSignedDisplayUrl } from '@/utils/imageUrlUtils';
import { formatSourcingPlatformLabel } from './sourcingBatchDisplay';

const CURRENCY_SYMBOL = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CNY: '¥',
  JPY: '¥',
};

/** 规范化列表 API 返回的采集条目 */
export function normalizeSourcingItemRow(row) {
  if (!row || typeof row !== 'object') return row;
  let imageUrls = row.imageUrls ?? row.image_urls;
  if (typeof imageUrls === 'string') {
    try {
      const parsed = JSON.parse(imageUrls);
      imageUrls = Array.isArray(parsed) ? parsed : [];
    } catch {
      imageUrls = [];
    }
  }
  if (!Array.isArray(imageUrls)) imageUrls = [];

  const productMetadata = row.productMetadata ?? row.product_metadata ?? {};

  return {
    ...row,
    imageUrls,
    externalKey: row.externalKey ?? row.external_key ?? '',
    listingText: row.listingText ?? row.listing_text ?? '',
    primaryImageUrl: row.primaryImageUrl ?? row.primary_image_url ?? '',
    primaryImageViewUrl: row.primaryImageViewUrl ?? row.primary_image_view_url ?? '',
    remotePrimaryImageUrl: row.remotePrimaryImageUrl ?? row.remote_primary_image_url ?? '',
    imageViewUrls: row.imageViewUrls ?? row.image_view_urls ?? [],
    batchPlatformKey: row.batchPlatformKey ?? row.batch_platform_key ?? '',
    productMetadata,
    marketplaceItemId: row.marketplaceItemId ?? row.marketplace_item_id ?? '',
    sourceItemUrl: row.sourceItemUrl ?? row.source_item_url ?? '',
    gradeLabel: row.gradeLabel ?? row.grade_label ?? '',
    scoreTotal: row.scoreTotal ?? row.score_total,
    isHot: row.isHot ?? row.is_hot,
    imageSpaceRecordId: row.imageSpaceRecordId ?? row.image_space_record_id ?? '',
    imageSpaceUrl: row.imageSpaceUrl ?? row.image_space_url ?? '',
  };
}

/** 采集条目关联的图片空间记录 ID（插件入库时已同步写入图片空间） */
export function getSourcingImageSpaceRecordId(row) {
  return String(row?.imageSpaceRecordId ?? row?.image_space_record_id ?? '').trim();
}

/** 图片空间内持久化 URL */
export function getSourcingImageSpaceUrl(row) {
  return String(row?.imageSpaceUrl ?? row?.image_space_url ?? '').trim();
}

function urlsLooselySame(a, b) {
  if (!a || !b) return false;
  const sa = String(a).trim();
  const sb = String(b).trim();
  if (sa === sb) return true;
  const pathA = sa.split('?')[0];
  const pathB = sb.split('?')[0];
  return pathA === pathB;
}

/** 商品轮播图：主图 + imageUrls 去重 */
export function getSourcingProductImageUrls(row) {
  const primary = row.primaryImageUrl || row.remotePrimaryImageUrl;
  const out = [];
  if (primary) out.push(primary);
  const extras = Array.isArray(row.imageUrls) ? row.imageUrls : [];
  for (const u of extras) {
    if (u && typeof u === 'string' && !out.some((x) => urlsLooselySame(x, u))) {
      out.push(u);
    }
  }
  return out.slice(0, 24);
}

export function getSourcingProductPrimaryImage(row) {
  const urls = getSourcingProductImageUrls(row);
  return urls[0] || '';
}

/** 主图对应的 API viewUrl（signed-only 下用于 <img src>） */
export function resolveSourcingImageViewUrl(row, rawUrl) {
  const raw = String(rawUrl || '').trim();
  if (!raw || !row) return '';
  const primary = String(row.primaryImageUrl || '').trim();
  const remote = String(row.remotePrimaryImageUrl || '').trim();
  if (primary && urlsLooselySame(primary, raw)) {
    return row.primaryImageViewUrl || row.primaryImageUrlDisplay || '';
  }
  if (remote && urlsLooselySame(remote, raw)) {
    return row.primaryImageViewUrl || row.primaryImageUrlDisplay || '';
  }
  const extras = Array.isArray(row.imageUrls) ? row.imageUrls : [];
  const views = Array.isArray(row.imageViewUrls) ? row.imageViewUrls : [];
  const idx = extras.findIndex((u) => urlsLooselySame(u, raw));
  if (idx >= 0 && views[idx]) return views[idx];
  return resolveSignedDisplayForUrl(raw, row);
}

export function getSourcingProductPrimaryImageViewUrl(row) {
  const url = getSourcingProductPrimaryImage(row);
  return url ? resolveSourcingImageViewUrl(row, url) : '';
}

/** 格式化插件 metadata 中的价格 */
export function formatSourcingProductPrice(meta = {}, { withSymbol = true } = {}) {
  const price = meta.price ?? meta.unitPrice;
  if (price == null || price === '') return '';
  const currency = String(meta.currency || '').trim().toUpperCase();
  const symbol = CURRENCY_SYMBOL[currency] || (currency ? `${currency} ` : '$');
  const num = Number(price);
  const formatted = Number.isFinite(num) ? num.toFixed(2) : String(price);
  return withSymbol ? `${symbol}${formatted}` : formatted;
}

/** 卡片/详情展示用销量文案 */
export function formatSourcingSalesText(meta = {}, t) {
  const text = meta.salesText ?? meta.sales_text;
  if (text != null && String(text).trim() !== '') return String(text).trim();
  const sales = meta.sales;
  if (sales == null || sales === '') return '';
  const n = Number(sales);
  if (Number.isFinite(n)) {
    if (!t) return String(n);
    if (n >= 10000) {
      const wan = n / 10000;
      const label = Number.isInteger(wan) ? String(wan) : wan.toFixed(1).replace(/\.0$/, '');
      return t('sourcingHub.salesWan', { value: label });
    }
    if (n >= 1000) return t('sourcingHub.salesThousands', { value: Math.round(n / 100) / 10 });
    return t('sourcingHub.salesCount', { count: n });
  }
  return String(sales);
}

/** 详情弹窗：平台展示名 */
export function resolveSourcingProductPlatform(row, t) {
  const meta = row?.productMetadata ?? {};
  const fromMeta = meta.platform ?? meta.platformKey;
  const label = fromMeta
    ? formatSourcingPlatformLabel(String(fromMeta), t)
    : (row?.batchPlatformKey ? formatSourcingPlatformLabel(String(row.batchPlatformKey), t) : '');
  return label || '—';
}

/** 详情弹窗：商品 ID */
export function resolveSourcingProductId(row) {
  const meta = row?.productMetadata ?? {};
  return (
    meta.productId ??
    meta.product_id ??
    row.marketplaceItemId ??
    row.externalKey ??
    ''
  );
}

/** 原商品页链接：优先插件 metadata.productUrl */
export function resolveSourcingProductUrl(row) {
  const meta = row?.productMetadata ?? {};
  const fromMeta = meta.productUrl ?? meta.product_url ?? meta.href ?? meta.url;
  if (fromMeta && /^https?:\/\//i.test(String(fromMeta))) {
    return String(fromMeta).trim();
  }
  const source = row?.sourceItemUrl ?? row?.source_item_url;
  if (source && /^https?:\/\//i.test(String(source))) {
    return String(source).trim();
  }
  return '';
}

function formatMetadataValue(key, value, t) {
  if (value == null || value === '') return null;
  if (key === 'isHot') {
    const yes = value === true || value === 'true' || value === 1;
    return yes ? (t ? t('sourcingHub.yes') : 'true') : (t ? t('sourcingHub.no') : 'false');
  }
  if (key === 'trends' && Array.isArray(value)) return value.filter(Boolean).join(' · ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/** 详情弹窗字段：插件 metadata + 分析字段 */
export const SOURCING_DETAIL_FIELD_DEFS = [
  { key: 'price', labelKey: 'fieldPrice', getValue: (row) => formatSourcingProductPrice(row.productMetadata ?? {}, { withSymbol: true }) },
  { key: 'sales', labelKey: 'fieldSales', getValue: (row, t) => formatSourcingSalesText(row.productMetadata ?? {}, t) },
  { key: 'platform', labelKey: 'fieldPlatform', getValue: (row, t) => resolveSourcingProductPlatform(row, t) },
  { key: 'productId', labelKey: 'fieldProductId', getValue: (row) => resolveSourcingProductId(row) || null },
  { key: 'originalPrice', labelKey: 'fieldOriginalPrice', getValue: (row) => {
    const meta = row.productMetadata ?? {};
    if (meta.originalPrice == null) return null;
    return formatSourcingProductPrice({ ...meta, price: meta.originalPrice });
  }},
  { key: 'rating', labelKey: 'fieldRating', getValue: (row, t) => {
    const meta = row.productMetadata ?? {};
    const rating = meta.rating;
    if (rating == null) return null;
    const rc = meta.reviewCount;
    return rc != null && t ? t('sourcingHub.ratingWithReviews', { rating, count: rc }) : String(rating);
  }},
  { key: 'category', labelKey: 'fieldCategory', metaKey: 'category' },
  { key: 'seller', labelKey: 'fieldSeller', metaKey: 'seller' },
  { key: 'rank', labelKey: 'fieldRank', metaKey: 'rank' },
  { key: 'trends', labelKey: 'fieldTrends', metaKey: 'trends' },
  { key: 'hotScore', labelKey: 'fieldHotScore', metaKey: 'hotScore' },
  { key: 'isHot', labelKey: 'fieldIsHot', metaKey: 'isHot' },
  { key: 'dataType', labelKey: 'fieldDataType', metaKey: 'dataType' },
];

export function buildSourcingDetailFields(row, t) {
  const meta = row?.productMetadata ?? {};
  const fields = [];
  const seen = new Set();
  const labelOf = (def) => (t ? t(`sourcingHub.${def.labelKey}`) : def.key);

  for (const def of SOURCING_DETAIL_FIELD_DEFS) {
    let value = def.getValue ? def.getValue(row, t) : null;
    if (value == null && def.metaKey) {
      value = formatMetadataValue(def.metaKey, meta[def.metaKey], t);
    }
    if (value == null || value === '' || value === '—') continue;
    const label = labelOf(def);
    if (seen.has(label)) continue;
    seen.add(label);
    fields.push({ label, value });
  }

  const grade = row.gradeLabel ?? row.grade_label;
  if (grade) fields.push({ label: t ? t('sourcingHub.fieldGrade') : 'grade', value: grade });
  const score = row.scoreTotal ?? row.score_total;
  if (score != null && Number.isFinite(Number(score))) {
    fields.push({ label: t ? t('sourcingHub.fieldScore') : 'score', value: Number(score).toFixed(1) });
  }

  const extraKeys = Object.keys(meta).filter(
    (k) => !['title', 'price', 'currency', 'sales', 'salesText', 'platform', 'productId', 'productUrl',
      'originalPrice', 'rating', 'reviewCount', 'category', 'seller', 'rank', 'trends', 'hotScore',
      'isHot', 'dataType'].includes(k),
  );
  for (const k of extraKeys) {
    const value = formatMetadataValue(k, meta[k], t);
    if (value == null || value === '') continue;
    fields.push({ label: k, value });
  }

  return fields;
}

function pushUniqueUrl(urls, seen, value) {
  const trimmed = String(value || '').trim();
  if (!trimmed || seen.has(trimmed)) return;
  seen.add(trimmed);
  urls.push(trimmed);
}

/** 批次列表缩略图地址（用于批量签名） */
export function collectBatchPreviewImageUrls(batches) {
  const urls = [];
  const seen = new Set();
  for (const batch of batches || []) {
    const previews = batch?.previewImageUrls ?? batch?.preview_image_urls ?? [];
    for (const url of previews) {
      pushUniqueUrl(urls, seen, url);
    }
  }
  return urls;
}

/** 采集条目全部图片地址（用于批量签名） */
export function collectItemImageUrls(items) {
  const urls = [];
  const seen = new Set();
  for (const row of items || []) {
    for (const url of getSourcingProductImageUrls(row)) {
      pushUniqueUrl(urls, seen, url);
    }
  }
  return urls;
}

/** 尚无 API viewUrl 的受管存储地址（避免重复 sign-batch） */
export function collectItemImageUrlsNeedingSign(items) {
  const urls = [];
  const seen = new Set();
  for (const row of items || []) {
    const imageUrls = getSourcingProductImageUrls(row);
    const viewUrls = Array.isArray(row.imageViewUrls) ? row.imageViewUrls : [];
    const primary = getSourcingProductPrimaryImage(row);
    const primaryView = getSourcingProductPrimaryImageViewUrl(row);
    imageUrls.forEach((url, index) => {
      if (!url || !isManagedStorageUrl(url)) return;
      if (urlsLooselySame(url, primary) && primaryView) return;
      if (viewUrls[index]) return;
      pushUniqueUrl(urls, seen, url);
    });
  }
  return urls;
}

function buildSourcingItemCacheRecord(row) {
  const raw = getSourcingProductPrimaryImage(row);
  if (!raw) return null;
  const view = getSourcingProductPrimaryImageViewUrl(row);
  if (!view) return null;
  return {
    id: row.id,
    url: raw,
    primaryImageUrl: raw,
    primaryImageViewUrl: view,
    viewUrl: view,
    displayUrl: view,
  };
}

/** 用 API 已返回的 viewUrl 预热签名缓存，卡片可立即渲染 */
export function primeSourcingItemsDisplayCache(items) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return;
  const records = [];
  for (const row of list) {
    const primaryRecord = buildSourcingItemCacheRecord(row);
    if (primaryRecord) records.push(primaryRecord);
    const imageUrls = getSourcingProductImageUrls(row);
    const viewUrls = Array.isArray(row.imageViewUrls) ? row.imageViewUrls : [];
    imageUrls.forEach((url, index) => {
      const view = viewUrls[index];
      if (url && view) {
        records.push({ url, viewUrl: view, displayUrl: view });
      }
    });
  }
  if (records.length) {
    warmDisplayCacheFromRecords(records, (item) => item.url || item.primaryImageUrl || '');
  }
}

/** 批次列表缩略图：API 已签名则写入缓存，其余后台 sign-batch */
export function primeSourcingBatchPreviewCache(batches) {
  const list = Array.isArray(batches) ? batches : [];
  if (!list.length) return;
  const records = [];
  const needSign = [];
  const seen = new Set();
  for (const batch of list) {
    const previews = batch?.previewImageUrls ?? batch?.preview_image_urls ?? [];
    for (const url of previews) {
      const trimmed = String(url || '').trim();
      if (!trimmed) continue;
      if (trimmed.includes('/api/storage/images/view')) {
        records.push({ url: trimmed, viewUrl: trimmed, displayUrl: trimmed });
      } else if (isManagedStorageUrl(trimmed)) {
        pushUniqueUrl(needSign, seen, trimmed);
      }
    }
  }
  if (records.length) {
    warmDisplayCacheFromRecords(records, (item) => item.url);
  }
  if (needSign.length) {
    void prefetchDisplayUrlsForListItems(needSign.map((url) => ({ url })), (item) => item.url);
  }
}

function sanitizeSourcingDownloadBaseName(row, index = 0) {
  const base = String(row?.title || row?.externalKey || `product-${row?.id ?? index}`)
    .slice(0, 60)
    .replace(/[^\w\u4e00-\u9fa5-]+/g, '_');
  return base || `image-${index + 1}`;
}

function extFromDownloadBlob(blob, url) {
  if (blob?.type === 'image/png') return 'png';
  if (blob?.type === 'image/webp') return 'webp';
  if (blob?.type === 'image/gif') return 'gif';
  const match = String(url || '').match(/\.(png|jpe?g|webp|gif)(\?|#|$)/i);
  if (match) return match[1].toLowerCase().replace('jpeg', 'jpg');
  return 'jpg';
}

/** 解析采集商品主图的可下载地址 */
export async function resolveSourcingProductDownloadUrl(row) {
  const raw = getSourcingProductPrimaryImage(row);
  if (!raw) return '';
  const syncView = getSourcingProductPrimaryImageViewUrl(row);
  const resolved = syncView || await resolveSignedDisplayUrl(raw, {
    viewUrl: row?.primaryImageViewUrl,
    displayUrl: row?.primaryImageUrlDisplay || row?.primaryImageViewUrl,
  });
  return sanitizeManagedDisplayResult(resolved, raw)
    || resolveSignedDisplayForUrl(raw, row)
    || '';
}

export async function downloadSourcingProductImage(row, { onError, t } = {}) {
  const url = await resolveSourcingProductDownloadUrl(row);
  if (!url) {
    onError?.(t ? t('sourcingHub.noPrimaryToDownload') : '');
    return false;
  }
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(t ? t('sourcingHub.downloadFailedStatus', { status: res.status }) : String(res.status));
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    const baseName = sanitizeSourcingDownloadBaseName(row);
    const ext = extFromDownloadBlob(blob, url);
    link.download = `${baseName}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch (e) {
    onError?.(e.message || (t ? t('sourcingHub.downloadFailed') : ''));
    return false;
  }
}

/**
 * 批量下载采集商品主图为 ZIP
 * @returns {Promise<{ downloaded: number, total: number } | null>}
 */
export async function downloadSourcingProductsZip(rows, { onError, zipFileName, t } = {}) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (list.length === 0) {
    onError?.(t ? t('sourcingHub.selectProductsFirst') : '');
    return null;
  }

  const resolvedItems = [];
  for (let i = 0; i < list.length; i += 1) {
    const row = list[i];
    const url = await resolveSourcingProductDownloadUrl(row);
    if (!String(url || '').trim()) continue;
    resolvedItems.push({
      row,
      url: String(url).trim(),
      name: sanitizeSourcingDownloadBaseName(row, i),
    });
  }

  if (resolvedItems.length === 0) {
    onError?.(t ? t('sourcingHub.noPrimaryForSelected') : '');
    return null;
  }

  try {
    const stamp = new Date().toISOString().slice(0, 10);
    const safeBatch = String(zipFileName || '').trim()
      || (t ? t('sourcingHub.sourcingZipFileName', { stamp, count: list.length }) : `${stamp}.zip`);
    const fileName = safeBatch.endsWith('.zip') ? safeBatch : `${safeBatch}.zip`;
    const recordId = `dl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    registerDownloadTaskAbort(recordId, new AbortController());

    const writtenPaths = new Set();
    const serverItems = [];
    for (let i = 0; i < resolvedItems.length; i += 1) {
      const item = resolvedItems[i];
      let path = `${item.name}.jpg`;
      let suffix = 1;
      while (writtenPaths.has(path)) {
        path = `${item.name}_${suffix}.jpg`;
        suffix += 1;
      }
      writtenPaths.add(path);
      serverItems.push({ url: item.url, path });
    }

    addDownloadCenterRecord({
      id: recordId,
      fileName,
      url: '',
      source: t ? t('sourcingHub.collect') : '',
      status: 'downloading',
      createdAt: new Date().toISOString(),
      completedAt: null,
      downloadedAt: null,
      error: null,
      itemCount: list.length,
      progress: 0,
    });

    const sourcingKnownBytes = collectKnownImageBytes(resolvedItems.map((row) => row.row));
    if (shouldUseServerZipDownload({
      itemCount: serverItems.length,
      estimatedBytes: estimateClientZipBytes({
        itemCount: serverItems.length,
        knownBytes: sourcingKnownBytes,
      }),
    })) {
      await submitServerDownloadTask({
        fileName,
        source: t ? t('sourcingHub.collect') : '',
        items: serverItems,
        recordId,
        required: true,
      });
      return { downloaded: serverItems.length, total: list.length, queued: true, serverSide: true };
    }

    return enqueueDownloadCenterJob(async () => {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const clientWritten = new Set();

      for (let i = 0; i < resolvedItems.length; i += 1) {
        if (isDownloadTaskCancelled(recordId)) break;
        const item = resolvedItems[i];
        try {
          const res = await fetch(item.url, { mode: 'cors', credentials: 'omit' });
          if (!res.ok) continue;
          const blob = await res.blob();
          const ext = extFromDownloadBlob(blob, item.url);
          let path = `${item.name}.${ext}`;
          let suffix = 1;
          while (clientWritten.has(path)) {
            path = `${item.name}_${suffix}.${ext}`;
            suffix += 1;
          }
          clientWritten.add(path);
          zip.file(path, blob);
          updateDownloadCenterProgress(recordId, Math.round(((i + 1) / resolvedItems.length) * 80));
        } catch (e) {
          console.warn('[采集批量下载] 单张失败', item.row?.id, e);
        }
      }

      if (clientWritten.size === 0) {
        throw new Error('FETCH_ALL_FAILED');
      }
      if (isDownloadTaskCancelled(recordId)) {
        return { downloaded: clientWritten.size, total: list.length, queued: true };
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      await queueBrowserZipDownload({
        blob: zipBlob,
        fileName,
        source: t ? t('sourcingHub.collect') : '',
        recordId,
        meta: { itemCount: clientWritten.size },
      });

      return { downloaded: clientWritten.size, total: list.length, queued: true };
    });
  } catch (e) {
    onError?.(e.message || (t ? t('sourcingHub.zipFailed') : ''));
    return null;
  }
}
